import { useCallback, useEffect, useRef } from "react";

// Calls the latest callback debounceTime ms after the last call, with that call's
// arguments, as lodash's debounce with leading: false did. A pending call is dropped on
// unmount and when debounceTime changes. The refs are read only when the returned
// function runs, never during render.
export const useDebouncedCallback = <TArgs extends unknown[]>(
  callback: (...args: TArgs) => unknown,
  debounceTime: number,
) => {
  const executableCallback = useRef(callback);
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    executableCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (pendingTimeout.current) clearTimeout(pendingTimeout.current);
      pendingTimeout.current = null;
    };
  }, [debounceTime]);

  return useCallback(
    (...args: TArgs) => {
      if (pendingTimeout.current) clearTimeout(pendingTimeout.current);
      pendingTimeout.current = setTimeout(() => {
        pendingTimeout.current = null;
        executableCallback.current(...args);
      }, debounceTime);
    },
    [debounceTime],
  );
};
